/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 *
 * Cloudinary Image Optimization Utilities
 * Generates optimized URLs with next-gen formats and quality settings
 */

interface CloudinaryTransformOptions {
  width?: number
  height?: number
  quality?: 'auto' | 'auto:low' | 'auto:good' | 'auto:best' | 'auto:eco' | number
  format?: 'auto' | 'webp' | 'avif' | 'jpg' | 'png'
  crop?: 'fill' | 'fit' | 'scale' | 'crop' | 'pad'
  gravity?: 'auto' | 'face' | 'center' | 'north' | 'south' | 'east' | 'west'
  dpr?: 1 | 2 | 3 | 'auto'
}

/**
 * Responsive breakpoints configuration with tiered quality and DPR auto
 * Using DPR auto instead of manual 2x variants for better efficiency
 */
export const RESPONSIVE_BREAKPOINTS = {
  // Mobile - eco quality for bandwidth conservation
  'mobile': { width: 480, quality: 'auto:eco', tier: 'mobile' },
  
  // Tablet Portrait - good quality balance
  'tablet-portrait': { width: 768, quality: 'auto:good', tier: 'tablet' },
  
  // Tablet Landscape - good quality
  'tablet-landscape': { width: 1024, quality: 'auto:good', tier: 'tablet' },
  
  // Laptop - good quality for productivity devices
  'laptop': { width: 1200, quality: 'auto:good', tier: 'desktop' },
  
  // Desktop - best quality for large screens
  'desktop': { width: 1439, quality: 'auto:best', tier: 'desktop' },
  
  // Desktop Wide - best quality for wide screens
  'desktop-wide': { width: 1619, quality: 'auto:best', tier: 'desktop' },
  
  // Desktop HD - best quality for high-res displays
  'desktop-hd': { width: 1919, quality: 'auto:best', tier: 'desktop' },
  
  // Desktop Ultra - best quality for ultra-wide displays
  'desktop-ultra': { width: 1920, quality: 'auto:best', tier: 'desktop' }
} as const

/**
 * Detects if a URL is from Cloudinary
 */
export const isCloudinaryUrl = (url: string): boolean => {
  return url.includes('cloudinary.com') || url.includes('res.cloudinary.com')
}

/**
 * Extracts the public ID from a Cloudinary URL
 */
export const extractCloudinaryPublicId = (url: string): string | null => {
  if (!isCloudinaryUrl(url)) return null
  
  // Match various Cloudinary URL patterns
  const patterns = [
    /\/image\/upload\/(?:v\d+\/)?(.+)\.(?:jpg|jpeg|png|gif|webp|avif)$/i,
    /\/image\/upload\/(?:[^/]+\/)*(.+)\.(?:jpg|jpeg|png|gif|webp|avif)$/i,
    /\/upload\/(?:v\d+\/)?(.+)\.(?:jpg|jpeg|png|gif|webp|avif)$/i
  ]
  
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) {
      return match[1]
    }
  }
  
  return null
}

/**
 * Generates an optimized Cloudinary URL with transformations
 */
export const generateOptimizedCloudinaryUrl = (
  originalUrl: string,
  options: CloudinaryTransformOptions = {}
): string => {
  if (!isCloudinaryUrl(originalUrl)) {
    return originalUrl
  }

  const publicId = extractCloudinaryPublicId(originalUrl)
  if (!publicId) {
    return originalUrl
  }

  // Extract cloud name from original URL
  const cloudNameMatch = originalUrl.match(/https?:\/\/res\.cloudinary\.com\/([^/]+)\//)
  if (!cloudNameMatch) {
    return originalUrl
  }
  
  const cloudName = cloudNameMatch[1]

  // Build transformation string with advanced optimizations
  const transformations: string[] = []
  
  // Quality - use auto:good by default, allow override
  const quality = options.quality || 'auto:good'
  transformations.push(`q_${quality}`)
  
  // Format - ALWAYS use auto for modern format delivery (WebP/AVIF)
  transformations.push('f_auto')
  
  // DPR - use auto for retina-aware delivery
  const dpr = options.dpr || 'auto'
  transformations.push(`dpr_${dpr}`)
  
  // Width and height
  if (options.width) {
    transformations.push(`w_${options.width}`)
  }
  if (options.height) {
    transformations.push(`h_${options.height}`)
  }
  
  // Crop mode - default to fill for responsive images
  const crop = options.crop || 'fill'
  transformations.push(`c_${crop}`)
  
  // Gravity - auto by default for intelligent cropping
  const gravity = options.gravity || 'auto'
  transformations.push(`g_${gravity}`)

  const transformationString = transformations.join(',')
  
  return `https://res.cloudinary.com/${cloudName}/image/upload/${transformationString}/${publicId}`
}

/**
 * Generates responsive Cloudinary URLs for all defined breakpoints with DPR auto
 */
export const generateResponsiveBreakpointUrls = (
  originalUrl: string,
  options: Omit<CloudinaryTransformOptions, 'width' | 'quality' | 'dpr'> = {}
): Record<string, { url: string; width: number; breakpoint: string; tier: string }> => {
  if (!isCloudinaryUrl(originalUrl)) {
    return {}
  }

  const formats: Record<string, { url: string; width: number; breakpoint: string; tier: string }> = {}

  Object.entries(RESPONSIVE_BREAKPOINTS).forEach(([breakpoint, config]) => {
    const url = generateOptimizedCloudinaryUrl(originalUrl, {
      ...options,
      width: config.width,
      quality: config.quality,
      dpr: 'auto',
      crop: options.crop || 'fill',
      gravity: options.gravity || 'auto'
    })

    formats[config.width] = {
      url,
      width: config.width,
      breakpoint,
      tier: config.tier
    }
  })

  return formats
}

/**
 * Creates a srcset string from responsive breakpoint URLs
 */
export const createResponsiveBreakpointSrcset = (
  originalUrl: string,
  options: Omit<CloudinaryTransformOptions, 'width' | 'quality' | 'dpr'> = {}
): string => {
  if (!isCloudinaryUrl(originalUrl)) {
    return ''
  }

  const formats = generateResponsiveBreakpointUrls(originalUrl, options)
  
  return Object.values(formats)
    .map(format => `${format.url} ${format.width}w`)
    .join(', ')
}

/**
 * Generates multiple optimized URLs for different screen sizes (legacy support)
 */
export const generateResponsiveCloudinaryUrls = (
  originalUrl: string,
  sizes: number[] = [480, 768, 1024, 1366, 1920],
  options: Omit<CloudinaryTransformOptions, 'width'> = {}
): string[] => {
  if (!isCloudinaryUrl(originalUrl)) {
    return [originalUrl]
  }

  return sizes.map(width => 
    generateOptimizedCloudinaryUrl(originalUrl, {
      ...options,
      width,
      dpr: 'auto', // Use DPR auto for all generated URLs
      crop: options.crop || 'fill',
      gravity: options.gravity || 'auto'
    })
  )
}

/**
 * Creates a srcset string from responsive URLs (legacy support)
 */
export const createOptimizedSrcset = (
  originalUrl: string,
  sizes: number[] = [480, 768, 1024, 1366, 1920],
  options: Omit<CloudinaryTransformOptions, 'width'> = {}
): string => {
  if (!isCloudinaryUrl(originalUrl)) {
    return ''
  }

  const urls = generateResponsiveCloudinaryUrls(originalUrl, sizes, options)
  
  return urls
    .map((url, index) => `${url} ${sizes[index]}w`)
    .join(', ')
}

/**
 * Updated optimization presets with modern formats and DPR auto
 */
export const OPTIMIZATION_PRESETS = {
  // High quality for hero images with best quality
  hero: {
    quality: 'auto:best' as const,
    crop: 'fill' as const,
    gravity: 'auto' as const,
    dpr: 'auto' as const
  },
  
  // Balanced quality for regular content
  content: {
    quality: 'auto:good' as const,
    crop: 'fill' as const,
    gravity: 'auto' as const,
    dpr: 'auto' as const
  },
  
  // Efficient quality for thumbnails and mobile-first content
  thumbnail: {
    quality: 'auto:eco' as const,
    crop: 'fill' as const,
    gravity: 'auto' as const,
    dpr: 'auto' as const
  },
  
  // Ultra-efficient preset for maximum bandwidth savings
  efficient: {
    quality: 'auto:eco' as const,
    crop: 'fit' as const,
    gravity: 'center' as const,
    dpr: 'auto' as const
  }
} 